import { getLinkFromCarouselTypeName, getProductTypeFromTypename } from 'bundles/collections-carousel/constants';
import type {
  RecommendationCarouselCollectionFragment,
  RecommendationCarouselProductCardFragment,
} from 'bundles/collections-carousel/queries/__generated__/RecommendationCarouselCollection';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';

const mapProductCardToCollectionEntity = (
  productCard: RecommendationCarouselProductCardFragment,
  recommendation: Pick<CollectionEntity, 'recommenderConfigurationHash' | 'context'>
): CollectionEntity | undefined => {
  const { productTypeAttributes } = productCard;
  let canonical;
  let imageUrl;
  if (productTypeAttributes.__typename === 'ProductCard_Course') {
    canonical = productTypeAttributes.canonical;
    imageUrl = canonical.promoPhotoUrl;
  } else if (productTypeAttributes.__typename === 'ProductCard_Specialization') {
    canonical = productTypeAttributes.canonical;
    imageUrl = canonical.cardImageUrl;
  } else {
    return undefined;
  }

  const productType = getProductTypeFromTypename(productCard.marketingProductType ?? undefined);
  const partners = canonical.partners.map((partner) => ({
    id: partner.id,
    name: partner.name,
    logo: partner.squareLogo ?? '',
  }));

  return {
    id: productCard.id,
    name: canonical.name,
    slug: canonical.slug,
    imageUrl: imageUrl ?? '',
    link: getLinkFromCarouselTypeName(canonical.slug, productType),
    partners,
    partnerLogos: partners.map((partner) => partner.logo),
    productType,
    statusTags: productCard.badges,
    ...recommendation,
  };
};

export const mapRecommendationCollectionForCarousel = (
  collection?: RecommendationCarouselCollectionFragment | null
): CarouselCollection | undefined => {
  if (!collection) return undefined;
  const context = (collection.context as Record<string, unknown> | null) ?? undefined;
  const recommenderConfigurationHash = collection.recommenderConfigurationHash ?? undefined;
  const entities = (collection.entities?.elements ?? [])
    .map((entity) =>
      entity?.__typename === 'ProductCard_ProductCard'
        ? mapProductCardToCollectionEntity(entity, { recommenderConfigurationHash, context })
        : undefined
    )
    .filter((entity): entity is CollectionEntity => entity !== undefined);

  return {
    collectionId: collection.id,
    title: collection.description ?? '',
    entities,
    ...(recommenderConfigurationHash && {
      recommender: { hash: recommenderConfigurationHash, ...(context && { context }) },
    }),
  };
};
